import { css } from '@emotion/react';
import Form, { FormInstance, useForm } from 'rc-field-form';
import React, { FC, useRef } from 'react';

import { SearchAddress } from 'api/api';

import { InputClearIconShell } from '~src/commons/components/text-input/input-clear-icon-shell';
import { useLogPosthogEvent } from '~src/commons/services/posthog-service';
import { useOutsideClick } from '~src/commons/utils/hooks';
import { useOutSideIframeClick } from '~src/commons/utils/hooks/checkout-hooks';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState } from '~src/global-state';
import { fontStyles } from '~src/theme/fontStyles';
import { Button } from '~src/ui/button';
import { TextInput } from '~src/ui/text-input';

import {
  inlinePostalCodeInputID,
  inlinePostalCodeSubmitButtonID,
} from '~address-form/utils/address-utils';

import { AddressProvider } from './address-provider';
import { PostalCodeField } from './postal-code-field';

interface FormComponentProps {
  formInstance: FormInstance;
  onFinish: (newAddress: SearchAddress) => void;
}

export const AddressInputInline: React.FC = () => {
  const globalState = useGlobalState();
  const [formInstance] = useForm();
  const logPosthogEvent = useLogPosthogEvent();

  const postalCode = globalState.search_address?.postal_code ?? '';

  const onClear = () => {
    // Note that there's additional focus management logic in `renderer.tsx` on modal close
    formInstance.setFieldsValue({
      postal_code: postalCode,
    });
  };

  // Reset the input if address changes externally.
  React.useEffect(onClear, [postalCode]);

  return (
    <AddressProvider onReset={onClear}>
      {({ onUpdate }) => (
        <FormComponent
          formInstance={formInstance}
          onFinish={newAddress => {
            logPosthogEvent('Inline Postal Code Changed', newAddress);
            onUpdate(newAddress);
          }}
        />
      )}
    </AddressProvider>
  );
};

const FormComponent: FC<FormComponentProps> = ({ formInstance, onFinish }) => {
  const { tICU } = useTranslations();

  const [isInEditState, setEditState] = React.useState(false);

  const state = useGlobalState();

  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const handleOutsideClick = () => {
    if (!isInEditState) {
      return;
    }

    if (
      !formInstance.getFieldValue('postal_code') ||
      (formInstance.getFieldValue('postal_code') && state.search_address?.postal_code)
    ) {
      setEditState(false);
      formInstance.resetFields();
    }
  };

  const wrapperRef = useOutsideClick(() => {
    handleOutsideClick();
  }, [isInEditState]);

  useOutSideIframeClick(() => {
    handleOutsideClick();
  }, [isInEditState]);

  const getInputLabel = () => {
    if (isInEditState) {
      return tICU('widget.ADDRESS_MODAL.POSTALCODE_PLACEHOLDER');
    }

    const postalCode = formInstance.getFieldValue('postal_code');
    if (postalCode) {
      // don't show placeholder when out of focus and have correct zipcode because there is
      // already label above inline zipcode input
      return '';
    }

    return tICU('widget.CUSTOMER_POSTALCODE_PLACEHOLDER');
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const clearButton = (
    <InputClearIconShell
      onClick={() => {
        formInstance.setFieldsValue({
          postal_code: '',
        });
        formInstance.validateFields();
        inputRef.current?.focus();
      }}
    />
  );

  return (
    <div ref={wrapperRef}>
      <Form
        initialValues={state.search_address}
        form={formInstance}
        onFinish={values => {
          onFinish(values);
          setEditState(false);
          inputRef.current?.blur();
        }}
      >
        <div css={styles.grid(isInEditState, buttonRef.current?.offsetWidth || 0)}>
          <PostalCodeField name="postal_code" required onFocus={() => setEditState(true)}>
            {inputProps => (
              <TextInput
                inputRef={inputRef}
                id={inlinePostalCodeInputID}
                label={getInputLabel()}
                raiseLabelStyle={{
                  top: '8px',
                }}
                style={{
                  padding: `${getInputLabel() ? '26px' : '10px'} 16px 10px 16px`,
                  ...fontStyles.px16_20,
                }}
                wrapperStyle={{ height: '50px' }}
                rightElement={_ => (isInEditState ? clearButton : undefined)}
                {...inputProps}
              />
            )}
          </PostalCodeField>
          <div css={styles.buttonWrapper(isInEditState)}>
            <Button
              ref={buttonRef}
              noWidth
              type="submit"
              size="small"
              id={inlinePostalCodeSubmitButtonID}
              data-test="address-submit-button"
            >
              {tICU('widget.SUBMIT')}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

const styles = {
  grid: (editable: boolean, buttonWidth: number) =>
    css({
      display: 'grid',
      width: '100%',
      transition: 'width 0.3s',
      gridTemplateColumns: editable ? `auto ${buttonWidth}px` : 'auto 0px',
      gap: editable ? '12px' : 0,
    }),
  buttonWrapper: (editable: boolean) =>
    css({
      opacity: editable ? 1 : 0,
      pointerEvents: editable ? 'all' : 'none',
      transition: 'opacity 0.3s',
    }),
};
